// ============================================================
// UserHistoryTab.js
// History tab inside <UserDetailPage>. Surfaces every audit row
// where this user is the subject. AuditLog scatters those rows
// across three EntityTypes:
//
//   - ClientUsers       — EntityID = ClientUserID directly
//   - ClientUserAgents  — EntityID = ClientUserID (composite-PK
//                          parent row; the audit writes record
//                          the user, not the (user, module) tuple)
//   - Invitations       — EntityID = InvitationID; the user is
//                          referenced via NewValues.redeemedClientUserID
//                          on 'redeemed' rows
//
// We fire three parallel /history queries scoped to this Client,
// merge the responses, then client-side filter the Invitations
// results to rows that point at this ClientUserID. Sort merged
// rows by changedAt descending. Server-side pagination doesn't
// span sources cleanly, so we request pageSize=200 per source
// (well above realistic per-user audit volume) and paginate the
// merged array client-side using the URL page/pageSize params.
//
// The "What" column would render the entity type and ID on every
// row, so it's replaced with a human-friendly Summary column
// derived from action + OldValues/NewValues. The full audit row
// remains viewable via the "View" link → JsonDialog.
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-clients-history.js
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DataTable from '../components/DataTable';
import Badge from '../components/Badge';
import JsonDialog from '../components/JsonDialog';
import { getAdminClientHistory } from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import { formatDateTime } from '../utils/format';
import './UserHistoryTab.css';

const DEFAULT_PAGE_SIZE = 50;
// Per-source fetch ceiling. The backend caps pageSize at 200;
// realistic per-user audit row counts are well below this for
// the foreseeable future. If a single user ever crosses 200
// audit rows in any one source, the History tab will silently
// truncate — revisit then with proper cross-source pagination.
const PER_SOURCE_FETCH_SIZE = 200;

const ACTION_OPTIONS = [
  { value: '',            label: 'All actions' },
  { value: 'created',     label: 'Created' },
  { value: 'updated',     label: 'Updated' },
  { value: 'deactivated', label: 'Deactivated' },
  { value: 'reactivated', label: 'Reactivated' },
  { value: 'deleted',     label: 'Deleted' },
  { value: 'revoked',     label: 'Revoked' },
  { value: 'redeemed',    label: 'Redeemed' },
  { value: 'granted',     label: 'Granted' },
  { value: 'expired',     label: 'Expired' },
];

const ACTION_VARIANT = {
  created:     'success',
  updated:     'blue',
  deactivated: 'orange',
  reactivated: 'teal',
  deleted:     'danger',
  revoked:     'danger',
  redeemed:    'success',
  granted:     'teal',
  expired:     'neutral',
};

function parsePageParam(raw, fallback) {
  if (raw == null) return fallback;
  const n = parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

function capitaliseAction(action) {
  if (!action) return '';
  return action.charAt(0).toUpperCase() + action.slice(1);
}

// Best-effort moduleKey extraction for granted/revoked rows.
// Backend writes { moduleId, moduleKey } in NewValues on grant
// and the same shape in OldValues on revoke; falls back if the
// shape ever drifts.
function pickModuleKey(values) {
  if (values && typeof values === 'object' && typeof values.moduleKey === 'string') {
    return values.moduleKey;
  }
  return null;
}

function summariseRow(row) {
  switch (row.action) {
    case 'created':     return 'User created';
    case 'updated':     return 'User updated';
    case 'deactivated': return 'User deactivated';
    case 'reactivated': return 'User reactivated';
    case 'deleted':     return 'User deleted';
    case 'redeemed':    return 'Invitation redeemed';
    case 'expired':     return 'Access expired';
    case 'granted': {
      const key = pickModuleKey(row.newValues);
      return key ? `Module access granted: ${key}` : 'Module access granted';
    }
    case 'revoked': {
      const key = pickModuleKey(row.oldValues);
      return key ? `Module access revoked: ${key}` : 'Module access revoked';
    }
    default:
      return row.action || '—';
  }
}

// Sort comparator — newest first. Rows with null changedAt sink
// to the bottom (shouldn't happen in practice; defensive only).
function byChangedAtDesc(a, b) {
  if (!a.changedAt && !b.changedAt) return 0;
  if (!a.changedAt) return 1;
  if (!b.changedAt) return -1;
  return b.changedAt.localeCompare(a.changedAt);
}

export default function UserHistoryTab({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const page     = parsePageParam(searchParams.get('page'), 1);
  const pageSize = parsePageParam(searchParams.get('pageSize'), DEFAULT_PAGE_SIZE);
  const action   = searchParams.get('action') || '';

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [detailRow, setDetailRow] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const baseParams = { page: 1, pageSize: PER_SOURCE_FETCH_SIZE };
      if (action) baseParams.action = action;

      const [clientUsersRes, agentsRes, invitationsRes] = await Promise.all([
        getAdminClientHistory(user.clientId, {
          ...baseParams,
          entityType: 'ClientUsers',
          entityId: user.clientUserId,
        }),
        getAdminClientHistory(user.clientId, {
          ...baseParams,
          entityType: 'ClientUserAgents',
          entityId: user.clientUserId,
        }),
        getAdminClientHistory(user.clientId, {
          ...baseParams,
          entityType: 'Invitations',
        }),
      ]);

      const invitationRows = (invitationsRes.items || []).filter((row) => (
        row.newValues
        && typeof row.newValues === 'object'
        && row.newValues.redeemedClientUserID === user.clientUserId
      ));

      const merged = [
        ...(clientUsersRes.items || []),
        ...(agentsRes.items || []),
        ...invitationRows,
      ].sort(byChangedAtDesc);

      setItems(merged);
      setTotalCount(merged.length);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [user.clientId, user.clientUserId, action]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleActionChange(value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('action', value); else next.delete('action');
      next.delete('page');
      return next;
    });
  }

  function handlePageChange(nextPage) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextPage === 1) next.delete('page'); else next.set('page', String(nextPage));
      return next;
    });
  }

  // Client-side pagination of the merged-and-filtered array.
  const pageStart = (page - 1) * pageSize;
  const pageItems = items.slice(pageStart, pageStart + pageSize);

  const columns = [
    {
      key: 'changedAt',
      label: 'When',
      render: (row) => (
        <span className="user-history__muted">{formatDateTime(row.changedAt)}</span>
      ),
      width: '210px',
    },
    {
      key: 'changedBy',
      label: 'Who',
      render: (row) => (
        <span className="user-history__who">{row.changedBy || '—'}</span>
      ),
      width: '200px',
    },
    {
      key: 'summary',
      label: 'Summary',
      render: (row) => <span className="user-history__summary">{summariseRow(row)}</span>,
    },
    {
      key: 'action',
      label: 'Action',
      render: (row) => (
        <Badge variant={ACTION_VARIANT[row.action] || 'neutral'}>
          {capitaliseAction(row.action)}
        </Badge>
      ),
      width: '120px',
    },
    {
      key: 'details',
      label: 'Details',
      render: (row) => (
        <button
          type="button"
          className="user-history__view"
          onClick={() => setDetailRow(row)}
        >
          View
        </button>
      ),
      width: '90px',
    },
  ];

  const isFiltered = Boolean(action);
  const emptyMessage = isFiltered
    ? 'No audit rows match this filter.'
    : 'No audit history for this user yet.';

  const dialogTitle = detailRow
    ? `${capitaliseAction(detailRow.action)} on ${detailRow.entityType}${
        detailRow.entityId != null ? ` #${detailRow.entityId}` : ''
      }`
    : '';

  return (
    <div className="user-history">
      <div className="user-history__toolbar">
        <label className="user-history__field">
          <span className="user-history__label">Action</span>
          <select
            value={action}
            onChange={(e) => handleActionChange(e.target.value)}
            className="user-history__select"
          >
            {ACTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
      </div>

      <DataTable
        data={pageItems}
        columns={columns}
        rowKey={(row) => row.auditLogId}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        loading={loading}
        error={error}
        onRetry={fetchData}
        emptyMessage={emptyMessage}
      />

      <JsonDialog
        isOpen={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        title={dialogTitle}
        sections={detailRow ? [
          { label: 'Old values', value: detailRow.oldValues },
          { label: 'New values', value: detailRow.newValues },
          { label: 'Details',    value: detailRow.details },
        ] : []}
      />
    </div>
  );
}

export { summariseRow };
